import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

import { fetchGogglesConnectivity } from '../services/reportsService';
import { useError } from '../contexts/ErrorContext';
import { VERSION_NOTES } from '../constants/versionNotes';
import { sortVersionsDesc } from '../utils/versionSort';
import { ANALYTICS_TIMEZONE, wallTimeToInstant, instantToWallTime, formatInTimeZone } from '../utils/timezone';
import {
  getCauseColor, getCauseInfo, getCauseCaveat, USERS_LINE_COLOR,
} from '../constants/connectivityCauses';
import Modal from './Modal';
import AnalyticsTimezoneNote from './AnalyticsTimezoneNote';
import ReconnectLoopRuleVisual from './ReconnectLoopRuleVisual';
import './GogglesConnectivityReport.css';

// Help-modal sections that render a component instead of prose. A cause's
// section opts in with `visual: '<key>'` in src/constants/connectivityCauses.js.
const CAUSE_VISUAL_SECTIONS = {
  reconnectLoopRule: () => <ReconnectLoopRuleVisual />,
};

const today = new Date().toISOString().slice(0, 10);
const defaultStart = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
})();

const DAY_TIME_FORMAT = { hour: 'numeric', minute: '2-digit' };

/** "2:14 PM" for a single-event day, "2:14 PM–5:03 PM" when errors span the day. */
const formatDayTimeRange = (d) => {
  if (!d?.first_time) return null;
  const first = formatInTimeZone(d.first_time, ANALYTICS_TIMEZONE, DAY_TIME_FORMAT);
  if (!d.last_time || d.last_time === d.first_time) return first;
  const last = formatInTimeZone(d.last_time, ANALYTICS_TIMEZONE, DAY_TIME_FORMAT);
  return first === last ? first : `${first}–${last}`;
};

const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allSelected = selected.length === options.length;
  const noneSelected = selected.length === 0;

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const buttonLabel = noneSelected
    ? `${label}: None`
    : allSelected
      ? `${label}: All`
      : `${label}: ${selected.length}/${options.length}`;

  return (
    <div className="gc-multiselect" ref={ref}>
      <button className="gc-multiselect-btn" onClick={() => setOpen(!open)}>
        {buttonLabel}
        <span className={`gc-multiselect-chevron ${open ? 'open' : ''}`}>&#9662;</span>
      </button>
      {open && (
        <div className="gc-multiselect-dropdown">
          <div className="gc-multiselect-actions">
            <button onClick={() => onChange([...options])}>All</button>
            <button onClick={() => onChange([])}>None</button>
          </div>
          <div className="gc-multiselect-options">
            {options.map(opt => (
              <label key={opt} className="gc-multiselect-option">
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => toggle(opt)}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Share of active skiers hit by none of the causes that day. Causes count a
// user at most once per cause per day, so a user hit by two causes on the
// same day is counted twice in the sum below — that can push the raw ratio
// past 100% affected, which is why this clamps to [0, 100] rather than
// trusting the arithmetic. null when there's no active-skier denominator to
// divide by (nothing to report, not "0% error-free").
const errorFreePct = (row, causes) => {
  const activeSkiers = row?.activeSkiers || 0;
  if (activeSkiers <= 0) return null;
  const totalAffected = causes.reduce((sum, c) => sum + (row.byCause?.[c.key]?.users || 0), 0);
  return Math.min(100, Math.max(0, 100 - (totalAffected / activeSkiers) * 100));
};

const ChartTooltip = ({ active, payload, label, causes, selectedCauseKey }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const freePct = errorFreePct(row, causes);
  return (
    <div className="gc-chart-tooltip">
      <div className="gc-chart-tooltip-date">{label}</div>
      <div className="gc-chart-tooltip-section">Affected users (1/user/day)</div>
      {causes.map((c) => {
        const users = row.byCause?.[c.key]?.users || 0;
        const dimmed = selectedCauseKey && selectedCauseKey !== c.key;
        return (
          <div key={c.key} className={`gc-chart-tooltip-row ${dimmed ? 'dim' : ''}`}>
            <span className="gc-chart-tooltip-swatch" style={{ background: getCauseColor(c.key) }} />
            <span className="gc-chart-tooltip-label">{c.label}</span>
            <span className="gc-chart-tooltip-value">{users.toLocaleString()}</span>
          </div>
        );
      })}
      <div className="gc-chart-tooltip-divider" />
      <div className="gc-chart-tooltip-row">
        <span className="gc-chart-tooltip-swatch" style={{ background: USERS_LINE_COLOR }} />
        <span className="gc-chart-tooltip-label">Active Skiers</span>
        <span className="gc-chart-tooltip-value">{(row.activeSkiers || 0).toLocaleString()}</span>
      </div>
      {freePct != null && (
        <div className="gc-chart-tooltip-row">
          <span className="gc-chart-tooltip-swatch" />
          <span className="gc-chart-tooltip-label">Error-free users</span>
          <span className="gc-chart-tooltip-value">{freePct.toFixed(2)}%</span>
        </div>
      )}
    </div>
  );
};

const GogglesConnectivityReport = () => {
  const { addError } = useError();
  const [searchParams, setSearchParams] = useSearchParams();
  const [startDate, setStartDate] = useState(() => searchParams.get('start') || defaultStart);
  const [endDate, setEndDate] = useState(() => searchParams.get('end') || today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  // Available filter options (populated from first unfiltered fetch)
  const [allIosVersions, setAllIosVersions] = useState([]);
  const [allAndroidVersions, setAllAndroidVersions] = useState([]);
  const [allFirmwareVersions, setAllFirmwareVersions] = useState([]);
  const [selectedIosVersions, setSelectedIosVersions] = useState([]);
  const [selectedAndroidVersions, setSelectedAndroidVersions] = useState([]);
  const [selectedFirmware, setSelectedFirmware] = useState([]);

  // Reconnect Loop Failure and Build Not Received only: drops rows at
  // rssi <= -87 (purely out-of-range) from every count — the closing
  // disconnect's reading for reconnect_loop, the preceding GogglesConnected's
  // reading for build. Default on; applied via Apply Filters like the version
  // filters, not live on toggle.
  const [excludeLowRssi, setExcludeLowRssi] = useState(true);

  // Firmware subset the data on screen was fetched with ([] = unfiltered).
  // Separate from `selectedFirmware`, which changes with the dropdown before
  // Apply Filters is pressed; the coverage notes must describe what was
  // actually applied.
  const [appliedFirmware, setAppliedFirmware] = useState([]);

  // A version subset pinned by a shared URL (?iosVersions=1.14.46,1.14.50).
  // Consumed once, the first time the full option lists are known — see the
  // population branch in handleFetch — then cleared so later fetches don't
  // keep re-narrowing a selection the user has since changed by hand.
  const urlIosVersionsRef = useRef(searchParams.get('iosVersions'));
  const urlAndroidVersionsRef = useRef(searchParams.get('androidVersions'));
  const urlFirmwareRef = useRef(searchParams.get('firmware'));

  // Whether the filter option lists have been populated for the current date
  // range. A ref, not derived from allIosVersions/allAndroidVersions state:
  // handleFetch re-invokes itself to apply a shared link's version subset, and
  // that nested call runs inside the *same* closure, where the state still
  // reads as empty. Checking state there made the nested call re-populate the
  // dropdowns and reset them to "All" while the data it fetched was filtered —
  // and the URL-sync effect then stripped the subset from the address bar.
  const optionsKnownRef = useRef(false);

  // A filter selection is only valid for the date range it was populated
  // from — reusing it after the range changes can silently zero every
  // cause if the old versions don't appear in the new range.
  const resetFilters = useCallback(() => {
    optionsKnownRef.current = false;
    setAllIosVersions([]);
    setAllAndroidVersions([]);
    setAllFirmwareVersions([]);
    setSelectedIosVersions([]);
    setSelectedAndroidVersions([]);
    setSelectedFirmware([]);
  }, []);

  // Cause + day selection (for the stacked chart and per-day drill-down)
  const [selectedCauseKey, setSelectedCauseKey] = useState(() => searchParams.get('cause') || null);
  const [showUsersLine, setShowUsersLine] = useState(true);
  const [infoCauseKey, setInfoCauseKey] = useState(null);

  const [selectedDay, setSelectedDay] = useState(null);

  // Which multi-day rows in Top Affected Users are expanded to show their
  // per-day links. Keyed by user_identifier; stale on a new fetch.
  const [expandedUsers, setExpandedUsers] = useState({});

  const clearDay = useCallback(() => {
    setSelectedDay(null);
  }, []);

  const handleFetch = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    setSelectedDay(null);
    setExpandedUsers({});
    try {
      // iosVersions/androidVersions are only sent once the option lists are
      // known — an empty array is a real filter value ("exclude this
      // platform"), not "no restriction", so they can't be sent before the
      // first unfiltered fetch has told us what's actually in this range.
      const versionsKnown = 'iosVersions' in overrides || optionsKnownRef.current;
      const filters = {
        ...(versionsKnown && {
          iosVersions: overrides.iosVersions ?? selectedIosVersions,
          androidVersions: overrides.androidVersions ?? selectedAndroidVersions,
        }),
        gogglesFirmwareVersions: overrides.gogglesFirmwareVersions ?? selectedFirmware,
        excludeLowRssi: overrides.excludeLowRssi ?? excludeLowRssi,
      };
      const result = await fetchGogglesConnectivity(startDate, endDate, addError, filters);

      // Populate filter dropdowns from first unfiltered fetch — iOS/Android
      // version lists come straight from the backend's availableVersions
      // (split by platform); firmware is still the union across every
      // cause's byGogglesSW.
      if (!optionsKnownRef.current && result.byCause) {
        const fwSet = new Set();
        for (const bucket of Object.values(result.byCause)) {
          for (const r of (bucket.byGogglesSW || [])) fwSet.add(r.goggles_sw_version);
        }
        const iosVers = sortVersionsDesc(result.availableVersions?.ios || []);
        const androidVers = sortVersionsDesc(result.availableVersions?.android || []);
        const fwVers = sortVersionsDesc([...fwSet]);
        if (iosVers.length || androidVers.length) {
          optionsKnownRef.current = true;
          setAllIosVersions(iosVers);
          setAllAndroidVersions(androidVers);
          setAllFirmwareVersions(fwVers);

          // Apply a shared URL's version subset, once, against the now-known
          // full lists — anything named in the URL that doesn't actually
          // appear in this date range is dropped rather than zeroing everything.
          const urlIosSubset = urlIosVersionsRef.current
            ? urlIosVersionsRef.current.split(',').filter((v) => iosVers.includes(v))
            : null;
          const urlAndroidSubset = urlAndroidVersionsRef.current
            ? urlAndroidVersionsRef.current.split(',').filter((v) => androidVers.includes(v))
            : null;
          const urlFwSubset = urlFirmwareRef.current
            ? urlFirmwareRef.current.split(',').filter((v) => fwVers.includes(v))
            : null;
          urlIosVersionsRef.current = null;
          urlAndroidVersionsRef.current = null;
          urlFirmwareRef.current = null;

          const initialIos = urlIosSubset?.length ? urlIosSubset : iosVers;
          const initialAndroid = urlAndroidSubset?.length ? urlAndroidSubset : androidVers;
          const initialFw = urlFwSubset?.length ? urlFwSubset : fwVers;
          setSelectedIosVersions(initialIos);
          setSelectedAndroidVersions(initialAndroid);
          setSelectedFirmware(initialFw);

          const iosIsFiltered = initialIos.length < iosVers.length;
          const androidIsFiltered = initialAndroid.length < androidVers.length;
          const fwIsFiltered = initialFw.length < fwVers.length;
          if (iosIsFiltered || androidIsFiltered || fwIsFiltered) {
            // Re-fetch with the URL's subset applied — same second call
            // "Apply Filters" makes after a manual selection. Awaited, and the
            // unfiltered `result` is never stored, so the spinner stays up
            // until the filtered data lands instead of flashing the unfiltered
            // numbers first.
            await handleFetch({
              iosVersions: initialIos,
              androidVersions: initialAndroid,
              gogglesFirmwareVersions: fwIsFiltered ? initialFw : [],
            });
            return;
          }
        }
      }

      setData(result);
      setAppliedFirmware(filters.gogglesFirmwareVersions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    startDate, endDate, addError, selectedIosVersions, selectedAndroidVersions, selectedFirmware,
    excludeLowRssi,
  ]);

  const handleFilterChange = useCallback((type, values) => {
    if (type === 'ios') setSelectedIosVersions(values);
    else if (type === 'android') setSelectedAndroidVersions(values);
    else setSelectedFirmware(values);
  }, []);

  const handleApplyFilters = useCallback(() => {
    if (!data) return;

    if ((selectedIosVersions.length === 0 && selectedAndroidVersions.length === 0) || selectedFirmware.length === 0) {
      setData(prev => ({
        ...prev,
        causeTotals: Object.fromEntries(
          (prev.causes || []).map(c => [c.key, { total_errors: 0, total_unique_users: 0 }])
        ),
        dailyByCause: [],
        dailyActiveSkiers: [],
        byCause: Object.fromEntries(
          (prev.causes || []).map(c => [c.key, { byAppVersion: [], byGogglesSW: [], errorsByUser: [], topUsers: [] }])
        ),
      }));
      return;
    }

    handleFetch({
      iosVersions: selectedIosVersions,
      androidVersions: selectedAndroidVersions,
      gogglesFirmwareVersions: selectedFirmware.length === allFirmwareVersions.length ? [] : selectedFirmware,
    });
  }, [data, handleFetch, selectedIosVersions, selectedAndroidVersions, selectedFirmware, allFirmwareVersions.length]);

  // Keep the URL in sync with the current view — dates, selected cause, and
  // version filters (only when they're a real subset; "all" is represented
  // by omitting the param) — so this exact report is a shareable link.
  // Merges with whatever else is already in the query string (e.g.
  // AnalyticsPage's `report` param) instead of replacing it.
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('start', startDate);
      next.set('end', endDate);

      if (selectedCauseKey) next.set('cause', selectedCauseKey);
      else next.delete('cause');

      const iosIsFiltered = allIosVersions.length > 0 && selectedIosVersions.length < allIosVersions.length;
      if (iosIsFiltered) next.set('iosVersions', selectedIosVersions.join(','));
      else next.delete('iosVersions');

      const androidIsFiltered = allAndroidVersions.length > 0 && selectedAndroidVersions.length < allAndroidVersions.length;
      if (androidIsFiltered) next.set('androidVersions', selectedAndroidVersions.join(','));
      else next.delete('androidVersions');

      const fwIsFiltered = allFirmwareVersions.length > 0 && selectedFirmware.length < allFirmwareVersions.length;
      if (fwIsFiltered) next.set('firmware', selectedFirmware.join(','));
      else next.delete('firmware');

      return next;
    }, { replace: true });
  }, [
    startDate, endDate, selectedCauseKey, selectedIosVersions, selectedAndroidVersions, selectedFirmware,
    allIosVersions.length, allAndroidVersions.length, allFirmwareVersions.length, setSearchParams,
  ]);

  // A URL carrying report state (a shared link) runs the report on load
  // instead of leaving the user to click "Run Report" themselves.
  const autoFetchedRef = useRef(false);
  useEffect(() => {
    if (autoFetchedRef.current) return;
    autoFetchedRef.current = true;
    const hasUrlState = ['start', 'end', 'cause', 'iosVersions', 'androidVersions', 'firmware'].some((k) => searchParams.has(k));
    if (hasUrlState) handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every breakdown's *ByDay field already came back with the range fetch
  // (see backend header comment), so selecting a day is a pure client-side
  // slice — no refetch, no loading gap, and the table can't disagree with
  // the chart since both read the same computation.
  const handleDayClick = useCallback((day) => {
    if (!day) return;
    setSelectedDay((prev) => (prev === day ? null : day));
  }, []);

  // Deep-link into a user's analytics for one specific day. The day boundaries
  // are resolved in ANALYTICS_TIMEZONE and sent as absolute instants, with `tz` so
  // the target page displays the same window — otherwise a bare "2026-08-21T00:00"
  // would be read in the viewer's own timezone and land on a different day.
  // `sessionId`, when given, opens that session's detail view directly instead
  // of leaving the day's session list for the user to search through by hand.
  // `endDay` widens the window past `day` when the session's events straddle
  // midnight — the target page has to load every day the session touches to
  // group its events under the same id this report sent.
  const getUserDayHref = useCallback((uid, day, sessionId = null, endDay = null) => {
    if (!uid || !day) return null;
    const start = wallTimeToInstant(`${day}T00:00`, ANALYTICS_TIMEZONE);
    const end = wallTimeToInstant(`${endDay && endDay > day ? endDay : day}T23:59`, ANALYTICS_TIMEZONE);
    if (!start || !end) return `/users/${uid}`;
    const params = new URLSearchParams({
      tab: 'analytics',
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      tz: ANALYTICS_TIMEZONE,
    });
    if (sessionId) params.set('session', sessionId);
    return `/users/${uid}?${params.toString()}`;
  }, []);

  // Deep-link straight to one session — every `by_session` entry names an
  // exact, unambiguous session, so this always carries a session id (unlike
  // the old per-day link, which had to fall back to a plain day view
  // whenever a day spanned more than one session).
  const getUserSessionHref = useCallback((uid, session) => (
    getUserDayHref(uid, session.day, session.session_id, session.end_day)
  ), [getUserDayHref]);

  // Fallback for a user whose errors carry no by_session breakdown at all —
  // the backend resolves events with no `ga_session_id` to the nearest real
  // session, so this only happens when the user has no session id anywhere in
  // the range. Rather than dropping all the way to a bare profile
  // link with no analytics context, link into the calendar day(s) spanning
  // their first-to-last error in this range, same convention as the old
  // per-day link.
  const getUserRangeHref = useCallback((uid, firstSeen, lastSeen) => {
    if (!uid || !firstSeen) return null;
    const startDay = instantToWallTime(firstSeen, ANALYTICS_TIMEZONE).slice(0, 10);
    const endDay = instantToWallTime(lastSeen || firstSeen, ANALYTICS_TIMEZONE).slice(0, 10);
    if (!startDay || !endDay) return `/users/${uid}`;
    const start = wallTimeToInstant(`${startDay}T00:00`, ANALYTICS_TIMEZONE);
    const end = wallTimeToInstant(`${endDay}T23:59`, ANALYTICS_TIMEZONE);
    if (!start || !end) return `/users/${uid}`;
    const params = new URLSearchParams({
      tab: 'analytics',
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      tz: ANALYTICS_TIMEZONE,
    });
    return `/users/${uid}?${params.toString()}`;
  }, []);

  // Whole-table (not per-row) link: honors the chart's day drill-down when one
  // is active, otherwise falls back to the user's plain profile page — a
  // specific error day, if there is exactly one, is linked directly from the
  // Top Affected Users row instead (see getUserDayHref usage there).
  const getUserHref = useCallback((uid) => {
    if (!uid) return null;
    if (selectedDay) return getUserDayHref(uid, selectedDay) || `/users/${uid}`;
    return `/users/${uid}`;
  }, [selectedDay, getUserDayHref]);

  // A row with no uid has no user page to open
  const handleUserClick = useCallback((e, user) => {
    if (!user.user_id) e.preventDefault();
  }, []);

  const causes = useMemo(() => data?.causes || [], [data]);
  const selectedCause = causes.find(c => c.key === selectedCauseKey);
  const dayCount = data?.dailyByCause?.length || 0;

  // Build the chart data: one row per day. Bars show distinct users per
  // cause per day (1/user/day). The right-axis line shows `activeSkiers` —
  // distinct users who fired GogglesConnected that day, i.e. the population
  // the bars are drawn from. Visual ratio = "what fraction of skiers hit X."
  const chartData = useMemo(() => {
    if (!data?.dailyByCause?.length) return [];
    const skiersByDate = new Map(
      (data.dailyActiveSkiers || []).map((d) => [d.date, d.active_skiers || 0])
    );
    return data.dailyByCause.map(d => ({
      date: d.date,
      byCause: d.byCause,
      activeSkiers: skiersByDate.get(d.date) || 0,
    }));
  }, [data]);

  // Explicit y-axis ceiling, shared by the axis itself and the invisible
  // "day click" filler bar below — see the filler's comment for why it needs
  // this rather than Recharts' auto domain.
  const chartMaxUsers = useMemo(() => {
    let max = 0;
    for (const row of chartData) {
      const stackTotal = causes.reduce((sum, c) => sum + (row.byCause?.[c.key]?.users || 0), 0);
      max = Math.max(max, stackTotal, row.activeSkiers || 0);
    }
    return max;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, causes]);

  // Per-cause "user-days" total = sum of daily distinct users for that cause
  // across the date range. Matches what the bar chart sums to so the cause
  // card and chart agree visually. Counted at most once per user per day.
  // Share of each cause's errors that carry a goggles firmware version, from
  // byGogglesSW (which the backend never filters by firmware, so it describes
  // the whole population even while a firmware filter is applied). A firmware
  // filter can only ever match that share: `goggles_sw_version` is on 0% of
  // GogglesBLEWriteFailed, ~46% of GogglesFailedToConnect, ~73% of
  // GogglesBuildNotReceived and ~78% of GogglesDisconnected events
  // (2026-03-16 → 09-26), so without this note a firmware subset silently
  // shrinks most causes and zeroes BLE Write Failed.
  const firmwareCoverage = useMemo(() => {
    const out = {};
    for (const c of causes) {
      let known = 0;
      let total = 0;
      for (const r of (data?.byCause?.[c.key]?.byGogglesSW || [])) {
        total += r.error_count || 0;
        if (r.goggles_sw_version !== 'unknown') known += r.error_count || 0;
      }
      out[c.key] = total > 0 ? known / total : null;
    }
    return out;
  }, [data, causes]);

  // Only a subset that leaves out 'unknown' drops version-less events.
  const firmwareFilterDropsUnknown = appliedFirmware.length > 0 && !appliedFirmware.includes('unknown');

  const causeUserDays = useMemo(() => {
    const out = {};
    for (const c of causes) out[c.key] = 0;
    for (const d of (data?.dailyByCause || [])) {
      for (const c of causes) {
        out[c.key] += d.byCause?.[c.key]?.users || 0;
      }
    }
    return out;
  }, [data, causes]);

  // When a day is selected, breakdown tables read the *ByDay slice of the
  // already-fetched range data instead of refetching — no loading gap, and
  // the table can't disagree with the bar since both come from the same
  // computation. Reshaped into the same `byCause[cause] = { byAppVersion,
  // byGogglesSW, errorsByUser, topUsers }` shape as the whole-range data so
  // the aggregation logic below doesn't need to know which case it's in.
  const scopedSnapshot = useMemo(() => {
    if (!selectedDay || !data?.byCause) return data;
    const byCause = {};
    for (const [key, bucket] of Object.entries(data.byCause)) {
      byCause[key] = {
        byAppVersion: bucket.byAppVersionByDay?.[selectedDay] || [],
        byGogglesSW: bucket.byGogglesSWByDay?.[selectedDay] || [],
        errorsByUser: bucket.errorsByUserByDay?.[selectedDay] || [],
        topUsers: bucket.topUsersByDay?.[selectedDay] || [],
      };
    }
    return { ...data, byCause };
  }, [data, selectedDay]);
  const causeLabel = selectedCause?.label || '';

  // Aggregate per-cause breakdowns when no cause is selected. unique_users sums
  // across causes — a user hit by multiple causes is counted in each, so the
  // total can exceed the true distinct count. Worth the simplicity for now;
  // an "All causes" badge surfaces the caveat in the UI.
  const aggregateByVersion = (snapshot, tableKey, versionKey) => {
    const out = new Map();
    for (const c of causes) {
      for (const row of (snapshot?.byCause?.[c.key]?.[tableKey] || [])) {
        const k = row[versionKey];
        if (!out.has(k)) out.set(k, { [versionKey]: k, error_count: 0, unique_users: 0 });
        const acc = out.get(k);
        acc.error_count += row.error_count || 0;
        acc.unique_users += row.unique_users || 0;
      }
    }
    return Array.from(out.values()).sort((a, b) => b.error_count - a.error_count);
  };

  const aggregateUsers = (snapshot, tableKey, includeMeta) => {
    const out = new Map();
    for (const c of causes) {
      for (const row of (snapshot?.byCause?.[c.key]?.[tableKey] || [])) {
        const k = row.user_identifier;
        if (!out.has(k)) {
          out.set(k, includeMeta ? {
            user_identifier: row.user_identifier,
            user_id: row.user_id || null,
            user_name: row.user_name || null,
            error_count: 0,
            first_seen: row.first_seen,
            last_seen: row.last_seen,
            app_versions: new Set(),
            device_models: new Set(),
            bySessionMap: new Map(),
          } : {
            user_identifier: row.user_identifier,
            user_id: row.user_id || null,
            user_name: row.user_name || null,
            error_count: 0,
          });
        }
        const acc = out.get(k);
        acc.error_count += row.error_count || 0;
        if (includeMeta) {
          if (row.first_seen && (!acc.first_seen || row.first_seen < acc.first_seen)) acc.first_seen = row.first_seen;
          if (row.last_seen && (!acc.last_seen || row.last_seen > acc.last_seen)) acc.last_seen = row.last_seen;
          for (const v of (row.app_versions || [])) acc.app_versions.add(v);
          for (const m of (row.device_models || [])) acc.device_models.add(m);
          // The same session can appear once per cause a user was hit by
          // within it — sum the counts and widen first/last time to the
          // earliest/latest across causes, rather than letting one cause's
          // row overwrite another's. Keyed by session_id itself, so unlike
          // the old day-keyed version there's no ambiguity to resolve: each
          // row already names the exact session it came from.
          for (const s of (row.by_session || [])) {
            const existing = acc.bySessionMap.get(s.session_id);
            if (!existing) {
              acc.bySessionMap.set(s.session_id, {
                session_id: s.session_id,
                day: s.day,
                error_count: s.error_count || 0,
                first_time: s.first_time,
                last_time: s.last_time,
              });
            } else {
              existing.error_count += s.error_count || 0;
              if (s.first_time && (!existing.first_time || s.first_time < existing.first_time)) existing.first_time = s.first_time;
              if (s.last_time && (!existing.last_time || s.last_time > existing.last_time)) existing.last_time = s.last_time;
            }
          }
        }
      }
    }
    const rows = Array.from(out.values());
    if (includeMeta) {
      for (const r of rows) {
        r.app_versions = [...r.app_versions];
        r.device_models = [...r.device_models];
        r.by_session = Array.from(r.bySessionMap.values())
          .sort((a, b) => a.first_time.localeCompare(b.first_time));
        delete r.bySessionMap;
      }
    }
    return rows.sort((a, b) => b.error_count - a.error_count);
  };

  const scopedTables = useMemo(() => {
    if (!scopedSnapshot) return null;
    if (selectedCauseKey) {
      const bucket = scopedSnapshot.byCause?.[selectedCauseKey];
      return bucket || { byAppVersion: [], byGogglesSW: [], errorsByUser: [], topUsers: [] };
    }
    return {
      byAppVersion: aggregateByVersion(scopedSnapshot, 'byAppVersion', 'app_version'),
      byGogglesSW:  aggregateByVersion(scopedSnapshot, 'byGogglesSW',  'goggles_sw_version'),
      errorsByUser: aggregateUsers(scopedSnapshot, 'errorsByUser', false),
      topUsers:     aggregateUsers(scopedSnapshot, 'topUsers',     true),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedSnapshot, selectedCauseKey, causes]);

  const ScopeChips = () => {
    if (!selectedDay && !selectedCause) return null;
    return (
      <span className="gc-scope-chips">
        {selectedCause && (
          <span className="gc-scope-chip" style={{ '--chip-color': getCauseColor(selectedCauseKey) }}>
            <span className="gc-scope-chip-swatch" style={{ background: getCauseColor(selectedCauseKey) }} />
            {causeLabel}
          </span>
        )}
        {!selectedCause && selectedDay && (
          <span className="gc-scope-chip">All causes</span>
        )}
        {selectedDay && (
          <span className="gc-scope-chip">
            {selectedDay}
            <button className="gc-scope-chip-clear" onClick={clearDay} title="Clear day filter">&times;</button>
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="gc-report">
      <div className="gc-header">
        <div className="gc-header-left">
          <div className="gc-title-section">
            <h2 className="gc-title">Goggles Connectivity</h2>
            <p className="gc-subtitle">
              {selectedCause?.description || 'BLE connectivity failure modes'}
            </p>
          </div>
        </div>
        <div className="gc-controls">
          <input
            type="date"
            className="gc-date-input"
            value={startDate}
            onChange={e => { setStartDate(e.target.value); resetFilters(); }}
          />
          <span className="gc-date-sep">to</span>
          <input
            type="date"
            className="gc-date-input"
            value={endDate}
            onChange={e => { setEndDate(e.target.value); resetFilters(); }}
          />
          <button className="gc-fetch-btn" onClick={() => handleFetch()} disabled={loading}>
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
      </div>

      <AnalyticsTimezoneNote timezone={data?.timezone} />

      {/* Filter bar — shown after first fetch */}
      {(allIosVersions.length > 0 || allAndroidVersions.length > 0) && (
        <div className="gc-filters">
          <MultiSelect
            label="iOS Version"
            options={allIosVersions}
            selected={selectedIosVersions}
            onChange={(vals) => handleFilterChange('ios', vals)}
          />
          <MultiSelect
            label="Android Version"
            options={allAndroidVersions}
            selected={selectedAndroidVersions}
            onChange={(vals) => handleFilterChange('android', vals)}
          />
          <MultiSelect
            label="Firmware"
            options={allFirmwareVersions}
            selected={selectedFirmware}
            onChange={(vals) => handleFilterChange('fw', vals)}
          />
          <label className="gc-rssi-filter" title="Only affects Reconnect Loop Failure and Build Not Received">
            <input
              type="checkbox"
              checked={excludeLowRssi}
              onChange={(e) => setExcludeLowRssi(e.target.checked)}
            />
            <span>Exclude disconnects due to low signal strength</span>
          </label>
          <button className="gc-filter-apply" onClick={handleApplyFilters} disabled={loading}>
            Apply Filters
          </button>
        </div>
      )}

      {firmwareFilterDropsUnknown && (
        <div className="gc-fw-filter-note">
          Firmware filter excludes every event with no reported version, including connects
          counted in Active Skiers. Select <code>unknown</code> to include them.
        </div>
      )}

      {/* Version notes bulletin */}
      <div className="gc-bulletin">
        <button className="gc-bulletin-toggle" onClick={() => setNotesOpen(!notesOpen)}>
          <span className={`gc-bulletin-chevron ${notesOpen ? 'open' : ''}`}>&#9662;</span>
          Version Notes
        </button>
        {notesOpen && (
          <div className="gc-bulletin-body">
            {VERSION_NOTES.map(({ version, note }) => (
              <div key={version} className="gc-bulletin-item">
                <span className="gc-bulletin-version">{version}</span>
                <span className="gc-bulletin-note">{note}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div className="gc-error">{error}</div>}

      {!data && !loading && !error && (
        <div className="gc-empty">Select a date range and click Run Report</div>
      )}

      {loading && (
        <div className="gc-loading">
          <div className="gc-spinner" />
          <p>Querying Analytics...</p>
        </div>
      )}

      {data && !loading && (
        <div className="gc-content">
          {/* Cause cards — one per registered cause */}
          <div className="gc-cause-cards">
            {causes.map(c => {
              const totals = data.causeTotals?.[c.key] || { total_errors: 0, total_unique_users: 0 };
              const isActive = c.key === selectedCauseKey;
              const color = getCauseColor(c.key);
              const caveat = getCauseCaveat(c.key);
              // Pure UI selection — toggling a cause card just swaps which slice of
              // byCause the breakdown tables render. No new BigQuery query.
              const activate = () => {
                setSelectedCauseKey((prev) => (prev === c.key ? null : c.key));
              };
              return (
                <div
                  key={c.key}
                  role="button"
                  tabIndex={0}
                  className={`gc-cause-card ${isActive ? 'active' : ''}`}
                  style={{ '--cause-color': color }}
                  onClick={activate}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      activate();
                    }
                  }}
                >
                  <button
                    type="button"
                    className="gc-cause-card-info-btn"
                    title={`What is ${c.label}?`}
                    aria-label={`Help: ${c.label}`}
                    onClick={(e) => { e.stopPropagation(); setInfoCauseKey(c.key); }}
                  >
                    ?
                  </button>
                  <div className="gc-cause-card-name">
                    <span className="gc-cause-card-swatch" style={{ background: color }} />
                    {c.label}
                  </div>
                  <div className="gc-cause-card-stats">
                    <span className="gc-cause-card-errors">{(causeUserDays[c.key] || 0).toLocaleString()}</span>
                    <span className="gc-cause-card-users">{totals.total_unique_users.toLocaleString()} unique</span>
                  </div>
                  <div className="gc-cause-card-sub">
                    {dayCount > 0 ? `${Math.round((causeUserDays[c.key] || 0) / dayCount)}/day avg` : '—'}
                  </div>
                  {caveat && <div className="gc-cause-card-caveat">{caveat}</div>}
                  {firmwareFilterDropsUnknown && firmwareCoverage[c.key] != null && firmwareCoverage[c.key] < 0.995 && (
                    <div className="gc-cause-card-fw-coverage">
                      {firmwareCoverage[c.key] === 0
                        ? 'Firmware filter hides all of these: no version on this event'
                        : `Firmware filter can match only ${Math.round(firmwareCoverage[c.key] * 100)}% of these; the rest have no version`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Combined chart + errors by user */}
          <div className="gc-chart-with-table">
            <div className="gc-chart-section gc-chart-with-table-chart">
              <div className="gc-chart-header">
                <h3 className="gc-section-title">
                  Daily Affected Users <span className="gc-section-sub">(stacked, 1/user/day) · Active Skiers (line — users who connected to goggles at least once that day)</span>
                </h3>
                <div className="gc-series-toggles">
                  <button
                    className={`gc-series-toggle ${showUsersLine ? 'active' : ''}`}
                    style={{ '--toggle-color': USERS_LINE_COLOR }}
                    onClick={() => setShowUsersLine(v => !v)}
                  >
                    Active Skiers
                  </button>
                </div>
              </div>
              <div className="gc-chart-container">
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 16, right: 24, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid stroke="#1f1f1f" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#444"
                      tick={({ x, y, payload }) => (
                        <g transform={`translate(${x},${y})`} style={{ cursor: 'pointer' }} onClick={() => handleDayClick(payload.value)}>
                          <text
                            dy={12}
                            textAnchor="middle"
                            fill={selectedDay === payload.value ? '#fff' : '#999'}
                            fontSize={11}
                            fontWeight={selectedDay === payload.value ? 600 : 400}
                            fontFamily="SF Mono, Monaco, monospace"
                          >
                            {payload.value ? payload.value.slice(5) : ''}
                          </text>
                        </g>
                      )}
                    />
                    {/* Single shared y-axis: stacked-cause users + active-skiers
                        line are all "users", so the visual height ratio is honest.
                        Domain is pinned explicitly (rather than Recharts' auto
                        domain) so it lines up exactly with the day-click filler
                        bar below, which needs to know the real ceiling. */}
                    <YAxis
                      yAxisId="left"
                      stroke="#444"
                      allowDecimals={false}
                      domain={[0, chartMaxUsers]}
                      tick={{ fill: '#999', fontSize: 11, fontFamily: 'SF Mono, Monaco, monospace' }}
                      label={{ value: 'users', position: 'insideTopLeft', fill: '#666', fontSize: 10, dy: -10 }}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                      content={(props) => <ChartTooltip {...props} causes={causes} selectedCauseKey={selectedCauseKey} />}
                    />
                    {causes.map(c => (
                      <Bar
                        key={c.key}
                        yAxisId="left"
                        dataKey={(row) => row.byCause?.[c.key]?.users || 0}
                        stackId="users"
                        name={c.label}
                        fill={getCauseColor(c.key)}
                        fillOpacity={selectedCauseKey && selectedCauseKey !== c.key ? 0.25 : 1}
                        // A cause with a handful of errors on a busy day can round
                        // to a near-zero-height segment — floor it to 2px so it
                        // stays visible and clickable instead of disappearing into
                        // the stack. Floor only a genuinely nonzero value, or every
                        // zero-value cause draws a 2px sliver too.
                        //
                        // The callback's first argument is unusable here: for a
                        // stacked bar Recharts passes `value[1]` — the cumulative
                        // TOP of the stack, not this segment's own height (see
                        // recharts/lib/cartesian/Bar.js, `minPointSizeCallback`).
                        // Every zero segment stacked above a non-zero one would
                        // read that as a positive number. `index` is the real data
                        // index, so read this cause's own value off the row.
                        minPointSize={(_stackTop, index) => (
                          (chartData[index]?.byCause?.[c.key]?.users || 0) > 0 ? 2 : 0
                        )}
                        isAnimationActive={false}
                        cursor="pointer"
                        onClick={(barData) => {
                          // Click a stack segment → select that cause AND drill into that day.
                          setSelectedCauseKey(c.key);
                          const day = barData?.payload?.date || barData?.date;
                          if (day) handleDayClick(day);
                        }}
                      />
                    ))}
                    {/* Invisible filler completing each day's stack up to
                        chartMaxUsers. A real cause segment can be only a couple
                        px tall — too thin to reliably click — so this fills the
                        rest of that day's column with a click target that scopes
                        to the day under whichever cause is already selected
                        (mirrors the axis-label click) rather than forcing a
                        specific cause. Sharing stackId="users" (rather than a
                        separate Bar) is what gives it the column's full width;
                        a sibling, unstacked Bar only gets a sliver next to the
                        real stack. */}
                    <Bar
                      yAxisId="left"
                      dataKey={(row) => {
                        const stackTotal = causes.reduce((sum, c) => sum + (row.byCause?.[c.key]?.users || 0), 0);
                        return Math.max(0, chartMaxUsers - stackTotal);
                      }}
                      stackId="users"
                      fill="transparent"
                      isAnimationActive={false}
                      cursor="pointer"
                      onClick={(barData) => {
                        const day = barData?.payload?.date || barData?.date;
                        if (day) handleDayClick(day);
                      }}
                    />
                    {showUsersLine && (
                      <Line
                        yAxisId="left"
                        dataKey="activeSkiers"
                        stroke={USERS_LINE_COLOR}
                        strokeWidth={2.5}
                        dot={{ stroke: USERS_LINE_COLOR, strokeWidth: 0, r: 3, fill: USERS_LINE_COLOR }}
                        activeDot={{ stroke: USERS_LINE_COLOR, strokeWidth: 0, r: 4.5, fill: USERS_LINE_COLOR }}
                        isAnimationActive={false}
                        name="Active Skiers"
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="gc-chart-section gc-chart-with-table-side">
              <h3 className="gc-section-title">
                Errors by User
                <ScopeChips />
              </h3>
              <div className="gc-table gc-table-errors-by-user">
                <div className="gc-table-header gc-table-header-errors-by-user">
                  <span>User</span>
                  <span>Errors</span>
                  <span></span>
                </div>
                <div className="gc-table-scroll">
                  {(scopedTables?.errorsByUser || []).map(user => (
                    <a key={user.user_identifier} href={getUserHref(user.user_id)} target="_blank" rel="noopener noreferrer" className={`gc-table-row gc-table-row-errors-by-user ${user.user_id ? 'gc-table-row-clickable' : ''}`} onClick={(e) => handleUserClick(e, user)}>
                      <span className="gc-table-cell-primary gc-user-name-cell">
                        {user.user_name || user.user_id || user.user_identifier.substring(0, 12) + '...'}
                      </span>
                      <span>{user.error_count.toLocaleString()}</span>
                      <span className="gc-copy-cell">
                        {user.user_id && (
                          <button
                            className="gc-copy-btn"
                            title="Copy user ID"
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(user.user_id); }}
                          >
                            &#x1F4CB;
                          </button>
                        )}
                      </span>
                    </a>
                  ))}
                </div>
                <div className="gc-table-row gc-table-row-errors-by-user gc-table-row-total">
                  <span className="gc-table-cell-primary">Total <span className="gc-table-cell-muted" style={{ fontWeight: 400, fontSize: 11 }}>(top 100)</span></span>
                  <span>{(scopedTables?.errorsByUser || []).reduce((sum, u) => sum + u.error_count, 0).toLocaleString()}</span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>

          {/* Distinct affected users (deduplicated across causes) */}
          <div className="gc-chart-section">
            <div className="gc-chart-header">
              <h3 className="gc-section-title">
                Distinct Affected Users
                <span className="gc-section-sub">any cause · 1/user/day, deduplicated across causes · Active Skiers (line — users who connected to goggles at least once that day)</span>
              </h3>
            </div>
            <div className="gc-chart-container gc-chart-container-short">
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart
                  data={(() => {
                    const skiersByDate = new Map(
                      (data?.dailyActiveSkiers || []).map((d) => [d.date, d.active_skiers || 0])
                    );
                    return (data?.dailyDistinctAffected || []).map(d => ({
                      date: d.date,
                      affected: d.affected_users || 0,
                      activeSkiers: skiersByDate.get(d.date) || 0,
                    }));
                  })()}
                  margin={{ top: 12, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke="#1f1f1f" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#444"
                    tick={{ fill: '#999', fontSize: 11, fontFamily: 'SF Mono, Monaco, monospace' }}
                    tickFormatter={(d) => d ? d.slice(5) : ''}
                  />
                  {/* Single shared y-axis: both metrics are "users", so the
                      bar's height honestly reflects its share of the line. */}
                  <YAxis
                    yAxisId="left"
                    stroke="#444"
                    allowDecimals={false}
                    tick={{ fill: '#999', fontSize: 11, fontFamily: 'SF Mono, Monaco, monospace' }}
                    label={{ value: 'users', position: 'insideTopLeft', fill: '#666', fontSize: 10, dy: -10 }}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const row = payload[0]?.payload;
                      if (!row) return null;
                      return (
                        <div className="gc-chart-tooltip">
                          <div className="gc-chart-tooltip-date">{label}</div>
                          <div className="gc-chart-tooltip-row">
                            <span className="gc-chart-tooltip-swatch" style={{ background: '#94a3b8' }} />
                            <span className="gc-chart-tooltip-label">Distinct affected users</span>
                            <span className="gc-chart-tooltip-value">{(row.affected || 0).toLocaleString()}</span>
                          </div>
                          <div className="gc-chart-tooltip-divider" />
                          <div className="gc-chart-tooltip-row">
                            <span className="gc-chart-tooltip-swatch" style={{ background: USERS_LINE_COLOR }} />
                            <span className="gc-chart-tooltip-label">Active Skiers</span>
                            <span className="gc-chart-tooltip-value">{(row.activeSkiers || 0).toLocaleString()}</span>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="affected"
                    fill="#94a3b8"
                    name="Distinct affected users"
                    isAnimationActive={false}
                  />
                  <Line
                    yAxisId="left"
                    dataKey="activeSkiers"
                    stroke={USERS_LINE_COLOR}
                    strokeWidth={2.5}
                    dot={{ stroke: USERS_LINE_COLOR, strokeWidth: 0, r: 3, fill: USERS_LINE_COLOR }}
                    activeDot={{ stroke: USERS_LINE_COLOR, strokeWidth: 0, r: 4.5, fill: USERS_LINE_COLOR }}
                    isAnimationActive={false}
                    name="Active Skiers"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Breakdowns side by side */}
          <div className="gc-breakdowns">
            <div className="gc-breakdown">
              <h3 className="gc-section-title">
                By App Version
                <ScopeChips />
              </h3>
              <div className="gc-table">
                <div className="gc-table-header">
                  <span>Version</span>
                  <span>Errors</span>
                  <span>Users</span>
                </div>
                {(scopedTables?.byAppVersion || []).map(row => (
                  <div key={row.app_version} className="gc-table-row">
                    <span className="gc-table-cell-primary">{row.app_version}</span>
                    <span>{row.error_count.toLocaleString()}</span>
                    <span>{row.unique_users.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="gc-breakdown">
              <h3 className="gc-section-title">
                By Goggles Firmware
                <ScopeChips />
              </h3>
              <div className="gc-table">
                <div className="gc-table-header">
                  <span>Firmware</span>
                  <span>Errors</span>
                  <span>Users</span>
                </div>
                {(scopedTables?.byGogglesSW || []).map(row => (
                  <div key={row.goggles_sw_version} className="gc-table-row">
                    <span className="gc-table-cell-primary">{row.goggles_sw_version}</span>
                    <span>{row.error_count.toLocaleString()}</span>
                    <span>{row.unique_users.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top affected users */}
          <div className="gc-chart-section">
            <h3 className="gc-section-title">
              Top Affected Users
              <ScopeChips />
            </h3>
            <div className="gc-table gc-table-wide">
              <div className="gc-table-header gc-table-header-users">
                <span>User</span>
                <span>Errors</span>
                <span>Devices</span>
                <span>App Versions</span>
                <span>Last Seen</span>
                <span></span>
              </div>
              {(scopedTables?.topUsers || []).slice(0, 20).map(user => {
                const sessions = user.by_session || [];
                const hasUid = !!user.user_id;
                // A single known session links straight there; more than one
                // makes the row an expand toggle instead of a direct link.
                const isMultiSession = hasUid && sessions.length > 1;
                const isExpanded = !!expandedUsers[user.user_identifier];
                // No session at all (every event lacked ga_session_id) falls
                // back to a day-scoped link rather than a bare profile page —
                // see getUserRangeHref.
                const singleSessionHref = !hasUid
                  ? getUserHref(user.user_id)
                  : sessions.length === 1
                    ? getUserSessionHref(user.user_id, sessions[0])
                    : sessions.length === 0
                      ? getUserRangeHref(user.user_id, user.first_seen, user.last_seen)
                      : null;

                const toggleExpand = () => {
                  setExpandedUsers(prev => ({ ...prev, [user.user_identifier]: !prev[user.user_identifier] }));
                };

                const RowTag = isMultiSession ? 'div' : 'a';
                const rowProps = isMultiSession
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      onClick: toggleExpand,
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(); }
                      },
                    }
                  : {
                      href: singleSessionHref,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                      onClick: (e) => handleUserClick(e, user),
                    };

                return (
                  <React.Fragment key={user.user_identifier}>
                    <RowTag
                      className={`gc-table-row gc-table-row-users ${hasUid ? 'gc-table-row-clickable' : ''} ${isMultiSession ? 'gc-table-row-expandable' : ''}`}
                      {...rowProps}
                    >
                      <span className="gc-table-cell-primary gc-user-name-cell">
                        {isMultiSession && <span className={`gc-expand-chevron ${isExpanded ? 'open' : ''}`}>&#9656;</span>}
                        {user.user_name || user.user_id || user.user_identifier.substring(0, 12) + '...'}
                      </span>
                      <span>
                        {user.error_count.toLocaleString()}
                        {isMultiSession && <span className="gc-table-cell-muted"> · {sessions.length} sessions</span>}
                      </span>
                      <span>{user.device_models.join(', ') || '-'}</span>
                      <span>{user.app_versions.join(', ') || '-'}</span>
                      <span className="gc-table-cell-muted">
                        {new Date(user.last_seen).toLocaleDateString()}
                      </span>
                      <span className="gc-copy-cell">
                        {user.user_id && (
                          <button
                            className="gc-copy-btn"
                            title="Copy user ID"
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigator.clipboard.writeText(user.user_id); }}
                          >
                            &#x1F4CB;
                          </button>
                        )}
                      </span>
                    </RowTag>
                    {isMultiSession && isExpanded && (
                      <div className="gc-table-row-days">
                        {sessions.slice().reverse().map(s => (
                          <a
                            key={s.session_id}
                            href={getUserSessionHref(user.user_id, s)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="gc-table-row-day"
                            onClick={(e) => handleUserClick(e, user)}
                          >
                            <span className="gc-table-row-day-date">
                              {s.day}
                              <span className="gc-table-row-day-time">{formatDayTimeRange(s)}</span>
                            </span>
                            <span className="gc-table-row-day-count">
                              {s.error_count.toLocaleString()} {s.error_count === 1 ? 'error' : 'errors'}
                            </span>
                          </a>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Help modal — content per cause from connectivityCauses.js (sourced from .md docs) */}
      {(() => {
        if (!infoCauseKey) return null;
        const c = causes.find((x) => x.key === infoCauseKey);
        const info = getCauseInfo(infoCauseKey);
        if (!c || !info?.sections?.length) return null;
        const color = getCauseColor(infoCauseKey);
        return (
          <Modal
            isOpen
            onClose={() => setInfoCauseKey(null)}
            title={c.label}
            subtitle={
              <span className="gc-info-modal-subtitle">
                <span className="gc-info-modal-swatch" style={{ background: color }} />
                Goggles Connectivity · cause <code>{c.key}</code>
              </span>
            }
            className="gc-info-modal"
          >
            {info.sections.map((s) => (
              <section key={s.title} className="gc-info-section">
                <h4>{s.title}</h4>
                {/* A section is either prose (`body`) or a rendered explainer
                    (`visual`) — see CAUSE_VISUAL_SECTIONS. */}
                {s.visual
                  ? CAUSE_VISUAL_SECTIONS[s.visual]?.()
                  : <div className="gc-info-section-body">{s.body}</div>}
              </section>
            ))}
          </Modal>
        );
      })()}
    </div>
  );
};

export default GogglesConnectivityReport;
